import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronDown, Download, Filter, Loader2, Plus, RotateCcw, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { onboardingService, type CaseExportParams } from '../services/onboardingService';
import { UserOptionPicker } from '../components/UserOptionPicker';
import {
  ONBOARDING_STATUS_LABELS,
  ONBOARDING_STATUS_VALUES,
  type CaseListParams,
  type CaseStatusValue,
  type OnboardingCase,
  type OnboardingStatus,
} from '../types/onboarding';
import type { WorkflowState, WorkflowSummary } from '../types/workflow';

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' },
  { value: 100, label: '100 per page' },
];

const STATUS_BADGE_CLASS: Partial<Record<OnboardingStatus, string>> = {
  onboarding_start: 'badge-info',
  review_phase: 'badge-warning',
  rejected: 'badge-error',
  onboarding_complete: 'badge-success',
};

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

const statusBadgeClass = (state: OnboardingCase['state']): string => {
  return STATUS_BADGE_CLASS[state as OnboardingStatus] ?? 'badge-info';
};

const statusLabel = (state: OnboardingCase['state']): string => {
  return ONBOARDING_STATUS_LABELS[state as OnboardingStatus] ?? humaniseStatus(String(state ?? ''));
};

function humaniseStatus(raw: string): string {
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Static-state fallback used when the reviewer's chosen workflow is
 * `ONB` but the workflows-states endpoint hasn't shipped yet. Never
 * shown for a non-ONB workflow — the picker stays empty rather than
 * suggest wrong values.
 */
const ONB_FALLBACK_STATUS_OPTIONS: Array<{ value: string; label: string }> =
  ONBOARDING_STATUS_VALUES.map((s) => ({ value: s, label: ONBOARDING_STATUS_LABELS[s] }));

/** Build the "New case" link, always carrying the workflow abbreviation. */
function buildNewCaseHref(workflowAbv: string): string {
  const base = '/onboarding/candidates/new';
  return workflowAbv ? `${base}?workflow_abv=${encodeURIComponent(workflowAbv)}` : base;
}

const DEFAULT_PAGE_SIZE = 20;

export const CasesPage = () => {
  const { showSuccess, showError, showInfo } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  /**
   * The "return URL" we pass through `Link` state to every child
   * page (detail, new-case). The child's Back / Cancel button
   * navigates to this URL, restoring the reviewer's filters
   * verbatim — including workflow tab, status, PA/PNC/RM,
   * pagination, and any active search query.
   *
   * Read at render time so it reflects the URL *right now*, not the
   * URL when the component first mounted. Middle-clicking a row to
   * open in a new tab loses this state (browser drops history-only
   * data), which is intentional — a fresh tab has no filtered
   * context to return to.
   */
  const returnToList = `${location.pathname}${location.search}`;

  // ── URL-driven state ─────────────────────────────────────────────
  //
  // Every filter is round-tripped through the URL so navigating to a
  // case detail and back preserves the reviewer's context (previously
  // reset — Bug 1). `useSearchParams` re-renders us on each URL
  // change, so downstream `useMemo`s stay in sync automatically.
  const q = searchParams.get('q') ?? '';
  const workflowAbv = searchParams.get('workflow_abv') ?? '';
  const status = (searchParams.get('status') ?? '') as CaseStatusValue | '';
  const pncOfficer = searchParams.get('assigned_pnc_officer') ?? '';
  const programAssociate = searchParams.get('assigned_program_associate') ?? '';
  const reportingManager = searchParams.get('reporting_manager') ?? '';
  const currentPage = parsePositiveInt(searchParams.get('page'), 1);
  const pageSize = parsePositiveInt(searchParams.get('page_size'), DEFAULT_PAGE_SIZE);

  /**
   * Apply a set of query-param edits atomically. Non-page edits
   * reset `page` to 1 (staying on page 5 after changing workflow
   * would land the reviewer somewhere useless). Uses
   * `{replace: true}` so back-button restores the previous URL, not
   * every intermediate keystroke.
   */
  const patchSearch = useCallback(
    (updates: Record<string, string | undefined>, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === undefined || value === '') next.delete(key);
            else next.set(key, value);
          }
          if (options?.resetPage !== false) {
            // Any filter change → back to page 1, unless the caller
            // is explicitly changing page.
            const isPageEdit = Object.prototype.hasOwnProperty.call(updates, 'page');
            if (!isPageEdit) next.delete('page');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Search input debounce ────────────────────────────────────────
  //
  // Search input tracks its own state so keystrokes don't spam the
  // URL. After 350ms of quiet, the debounced value writes to the URL
  // (which then triggers a refetch). Also syncs back from URL when
  // the reviewer navigates externally or clears filters.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const t = setTimeout(() => patchSearch({ q: trimmed || undefined }), 350);
    return () => clearTimeout(t);
  }, [searchInput, q, patchSearch]);

  const [showFilters, setShowFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ── Workflow registry ────────────────────────────────────────────
  //
  // Powers the workflow tab strip, status-dropdown source, and Add-
  // case CTA labelling.
  const workflowsQuery = useQuery({
    queryKey: ['onboarding-workflows'],
    queryFn: () => onboardingService.getWorkflows(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const workflows: WorkflowSummary[] = workflowsQuery.data ?? [];
  const listableWorkflows = useMemo(
    () => workflows.filter((w) => w.can_list !== false),
    [workflows],
  );
  const initiatableWorkflows = useMemo(
    () => workflows.filter((w) => w.can_initiate === true),
    [workflows],
  );

  // Auto-select the first listable workflow when the URL doesn't
  // specify one — Bug 2. Falls back to nothing when the reviewer
  // truly has zero listable workflows, and the empty-state below
  // takes over.
  useEffect(() => {
    if (workflowAbv) return;
    if (listableWorkflows.length === 0) return;
    const target = listableWorkflows[0].abbreviation;
    if (!target) return;
    patchSearch({ workflow_abv: target });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowAbv, listableWorkflows.length]);

  // When the workflow changes we reset the status filter — state
  // titles are workflow-specific and a stale value from a different
  // workflow filters nothing.
  const previousWorkflowAbv = useRef<string>(workflowAbv);
  useEffect(() => {
    if (previousWorkflowAbv.current === workflowAbv) return;
    previousWorkflowAbv.current = workflowAbv;
    if (status) patchSearch({ status: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowAbv]);

  // ── Workflow states ──────────────────────────────────────────────
  const statesQuery = useQuery({
    queryKey: ['onboarding-workflow-states', workflowAbv],
    queryFn: () => onboardingService.getWorkflowStates(workflowAbv),
    enabled: Boolean(workflowAbv),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const workflowStates: WorkflowState[] = statesQuery.data ?? [];
  const statusOptions = useMemo(() => {
    if (workflowAbv && workflowStates.length > 0) {
      return workflowStates.map((s) => ({
        value: s.title,
        label: s.label || ONBOARDING_STATUS_LABELS[s.title as OnboardingStatus] || humaniseStatus(s.title),
      }));
    }
    // Fallback only for ONB (or when the registry itself is offline
    // and we have no workflow context) so a CR reviewer never sees
    // ONB state suggestions.
    if (!workflowAbv || workflowAbv.toUpperCase() === 'ONB') {
      return ONB_FALLBACK_STATUS_OPTIONS;
    }
    return [];
  }, [workflowAbv, workflowStates]);

  // Currently-selected workflow record — used for the Add-case label
  // and the tab-strip active state.
  const selectedWorkflow = useMemo(
    () => listableWorkflows.find((w) => w.abbreviation === workflowAbv),
    [listableWorkflows, workflowAbv],
  );

  const params = useMemo<CaseListParams>(
    () => ({
      q: q || undefined,
      workflow_abv: workflowAbv || undefined,
      status: status || undefined,
      assigned_pnc_officer: pncOfficer || undefined,
      assigned_program_associate: programAssociate || undefined,
      reporting_manager: reportingManager || undefined,
      page: currentPage,
      page_size: pageSize,
    }),
    [
      q,
      workflowAbv,
      status,
      pncOfficer,
      programAssociate,
      reportingManager,
      currentPage,
      pageSize,
    ],
  );

  const activeFilterCount =
    (status ? 1 : 0) +
    (pncOfficer ? 1 : 0) +
    (programAssociate ? 1 : 0) +
    (reportingManager ? 1 : 0);

  const resetFilters = useCallback(() => {
    // Clear everything except workflow (which lives in the tab
    // strip) and pagination (implicit reset via patchSearch).
    patchSearch({
      status: undefined,
      assigned_pnc_officer: undefined,
      assigned_program_associate: undefined,
      reporting_manager: undefined,
    });
  }, [patchSearch]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['onboarding-cases', params],
    queryFn: () => onboardingService.getCases(params),
    // Skip the fetch while we're still determining a workflow to
    // send — prevents an initial bare-`/list` request (which the
    // server would default to ONB for users who may not have ONB
    // access) racing with the auto-select effect above.
    enabled: workflowAbv !== '' || listableWorkflows.length === 0,
    placeholderData: (previous) => previous,
  });

  const handleExport = async () => {
    if (isExporting) return;
    const exportParams: CaseExportParams = {
      q: params.q,
      workflow_abv: params.workflow_abv,
      status: params.status,
      assigned_pnc_officer: params.assigned_pnc_officer,
      assigned_program_associate: params.assigned_program_associate,
      reporting_manager: params.reporting_manager,
    };
    setIsExporting(true);
    try {
      await onboardingService.exportCases(exportParams);
      showSuccess('Export Started', 'CSV download has been triggered.');
    } catch (err: any) {
      const code = (err as any)?.serverError?.error;
      const message = err?.message || 'Could not export cases. Please try again.';
      if (code === 'NO_DATA' || err?.status === 404) {
        showInfo('Nothing to Export', message);
      } else if (code === 'EXPORT_LIMIT_EXCEEDED' || err?.status === 400) {
        showError(
          'Too Many Cases',
          'More than 10,000 cases match the current filters. Narrow the filters and try again.',
        );
      } else {
        showError('Export Failed', message);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const cases: OnboardingCase[] = data?.list ?? [];
  const pageInfo = data?.pageInfo;
  const total = pageInfo?.total ?? 0;
  const totalPages = pageInfo?.totalPages ?? 0;
  const showingFrom = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = Math.min(currentPage * pageSize, total);

  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-rgf-gray-900 mb-2">Error Loading Cases</h3>
        <p className="text-rgf-gray-600 mb-4">
          {(error as any)?.message || 'Failed to load cases.'}
        </p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Workflow tab strip — sits above every other control so the
          selected workflow is unambiguously the page's context. */}
      <WorkflowTabStrip
        loading={workflowsQuery.isLoading}
        workflows={listableWorkflows}
        activeAbv={workflowAbv}
        onSelect={(abv) => patchSearch({ workflow_abv: abv })}
      />

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="secondary"
          icon={
            isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )
          }
          onClick={handleExport}
          disabled={isExporting || !workflowAbv}
          title={
            workflowAbv
              ? `Download all matching ${selectedWorkflow?.label || workflowAbv} cases as CSV`
              : 'Pick a workflow above to enable export'
          }
        >
          {isExporting ? 'Exporting…' : 'Export CSV'}
        </Button>
        <StartWorkflowButton
          workflowsLoaded={!workflowsQuery.isLoading}
          initiatableWorkflows={initiatableWorkflows}
          selectedAbv={workflowAbv}
          selectedLabel={selectedWorkflow?.label}
          returnToList={returnToList}
        />
      </div>

      <div className="card">
        <div className="card-body space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name, email, or phone…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                startIcon={<Search className="h-4 w-4" />}
              />
            </div>
            <Button
              variant="secondary"
              icon={<Filter className="h-4 w-4" />}
              onClick={() => setShowFilters((v) => !v)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          </div>

          {showFilters && (
            <div className="space-y-4 pt-4 border-t border-rgf-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">Status</label>
                  <select
                    value={status}
                    onChange={(e) =>
                      patchSearch({ status: e.target.value || undefined })
                    }
                    disabled={Boolean(workflowAbv) && statesQuery.isLoading}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 disabled:bg-rgf-gray-50 disabled:text-rgf-gray-500"
                  >
                    <option value="">
                      {workflowAbv && statesQuery.isLoading ? 'Loading states…' : 'All Statuses'}
                    </option>
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <UserOptionPicker
                  label="PNC Officer"
                  value={pncOfficer}
                  onChange={(v) => patchSearch({ assigned_pnc_officer: v || undefined })}
                  specificType="pncOfficersOptions"
                />

                <UserOptionPicker
                  label="Program Associate"
                  value={programAssociate}
                  onChange={(v) => patchSearch({ assigned_program_associate: v || undefined })}
                  specificType="programAssociateOptions"
                />

                <UserOptionPicker
                  label="Reporting Manager"
                  value={reportingManager}
                  onChange={(v) => patchSearch({ reporting_manager: v || undefined })}
                  specificType="programAssociateOptions"
                />

                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Page Size
                  </label>
                  <select
                    value={pageSize}
                    onChange={(e) => patchSearch({ page_size: e.target.value })}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<RotateCcw className="h-4 w-4" />}
                    onClick={resetFilters}
                  >
                    Clear all filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-rgf-gray-200">
            <thead>
              <tr>
                <th className="table-header">Candidate</th>
                <th className="table-header">Contact</th>
                <th className="table-header">Job</th>
                <th className="table-header">PNC Officer</th>
                <th className="table-header">Program Associate</th>
                <th className="table-header">Reporting Manager</th>
                <th className="table-header">Status</th>
                <th className="table-header">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-rgf-gray-200">
              {isLoading && cases.length === 0 ? (
                <tr>
                  <td className="table-cell text-center text-rgf-gray-500" colSpan={8}>
                    Loading cases…
                  </td>
                </tr>
              ) : cases.length === 0 ? (
                <tr>
                  <td className="table-cell text-center text-rgf-gray-500" colSpan={8}>
                    {!workflowAbv && listableWorkflows.length === 0
                      ? 'No workflows are available for your account.'
                      : 'No cases found for the current filters.'}
                  </td>
                </tr>
              ) : (
                cases.map((c) => (
                  <tr key={c.id} className="hover:bg-rgf-gray-50">
                    <td className="table-cell">
                      <Link
                        to={`/onboarding/candidates/${c.id}`}
                        state={{ from: returnToList }}
                        className="text-sm font-medium text-rgf-primary-700 hover:text-rgf-primary-800 hover:underline"
                      >
                        {c.contact?.name || `Candidate #${c.id}`}
                      </Link>
                      <div className="text-xs text-rgf-gray-500">Case #{c.id}</div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-rgf-gray-900">{c.contact?.email || '—'}</div>
                      <div className="text-xs text-rgf-gray-500">{c.contact?.phone_number || '—'}</div>
                    </td>
                    <td className="table-cell">
                      {c.job ? (
                        <>
                          <div className="text-sm text-rgf-gray-900">{c.job.title || '—'}</div>
                          <div className="text-xs text-rgf-gray-500">
                            {[c.job.program, c.job.department].filter(Boolean).join(' • ') || '—'}
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-rgf-gray-500">—</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-rgf-gray-900">{c.assigned_pnc_officer || '—'}</div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-rgf-gray-900">{c.assigned_program_associate || '—'}</div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-rgf-gray-900">{c.reporting_manager || '—'}</div>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${statusBadgeClass(c.state)}`}>{statusLabel(c.state)}</span>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-rgf-gray-900">{formatDate(c.created_at)}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-rgf-gray-700">
                Showing {showingFrom} to {showingTo} of {total} cases
                {isFetching && <span className="ml-2 text-rgf-gray-400">(refreshing…)</span>}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => patchSearch({ page: String(currentPage - 1) })}
                >
                  Previous
                </Button>
                <span className="text-sm text-rgf-gray-700">
                  Page {currentPage}
                  {totalPages ? ` of ${totalPages}` : ''}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={totalPages > 0 ? currentPage >= totalPages : true}
                  onClick={() => patchSearch({ page: String(currentPage + 1) })}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Workflow tabs at the top of the page. Serves two purposes:
 *
 *   1. Communicates the current page context unambiguously ("we're
 *      looking at CR cases right now"). Previously the workflow
 *      selector lived inside the collapsed filter panel — reviewers
 *      often didn't realise which workflow they were filtering.
 *   2. Gives each workflow its own filter / add-case scope. Clicking
 *      a tab resets `status` (the state titles are workflow-specific)
 *      but preserves the other filters — that's usually what the
 *      reviewer wants when hopping between workflows.
 *
 * Collapses to a compact heading when only one workflow is listable
 * (a lone tab reads as noise). Hides entirely while the registry is
 * still loading.
 */
function WorkflowTabStrip({
  loading,
  workflows,
  activeAbv,
  onSelect,
}: {
  loading: boolean;
  workflows: WorkflowSummary[];
  activeAbv: string;
  onSelect: (abv: string) => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-rgf-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading workflows…
      </div>
    );
  }
  if (workflows.length === 0) return null;
  if (workflows.length === 1) {
    const w = workflows[0];
    return (
      <div className="text-sm font-medium text-rgf-gray-900">
        {w.label || w.abbreviation} cases
      </div>
    );
  }
  return (
    <div role="tablist" className="flex flex-wrap gap-1 border-b border-rgf-gray-200">
      {workflows.map((w) => {
        const isActive = w.abbreviation === activeAbv;
        return (
          <button
            key={w.abbreviation}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(w.abbreviation)}
            className={clsx(
              'inline-flex items-center gap-2 rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition',
              isActive
                ? 'border-rgf-primary-500 text-rgf-primary-700'
                : 'border-transparent text-rgf-gray-500 hover:text-rgf-gray-900',
            )}
          >
            {w.label || w.abbreviation}
          </button>
        );
      })}
    </div>
  );
}

/**
 * "Add case" affordance. Behaviour depends on which workflow(s) the
 * reviewer may initiate:
 *
 *   - **0 initiatable**  → button hidden entirely.
 *   - **1 initiatable**  → direct link, labelled with that workflow.
 *   - **N initiatable**  → dropdown listing every initiatable
 *                          workflow. The list-page's currently-active
 *                          workflow (if any) is the primary click
 *                          target on the button itself, so the
 *                          reviewer only needs to open the menu when
 *                          starting a case for a *different* workflow
 *                          than they're viewing.
 *
 * Falls back to a plain "New Case" link while the workflow registry
 * is still loading, so a registry outage doesn't block case
 * creation.
 */
function StartWorkflowButton({
  workflowsLoaded,
  initiatableWorkflows,
  selectedAbv,
  selectedLabel,
  returnToList,
}: {
  workflowsLoaded: boolean;
  initiatableWorkflows: WorkflowSummary[];
  selectedAbv: string;
  selectedLabel: string | undefined;
  /**
   * URL the child page's Cancel / post-create nav should return to.
   * Carries the currently-selected workflow tab + filter state so
   * the reviewer doesn't lose context when they Cancel out of a
   * create flow they opened by mistake.
   */
  returnToList: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  if (!workflowsLoaded) {
    return (
      <Link to={buildNewCaseHref(selectedAbv)} state={{ from: returnToList }}>
        <Button icon={<Plus className="h-4 w-4" />}>New Case</Button>
      </Link>
    );
  }
  if (initiatableWorkflows.length === 0) return null;

  if (initiatableWorkflows.length === 1) {
    const w = initiatableWorkflows[0];
    return (
      <Link to={buildNewCaseHref(w.abbreviation)} state={{ from: returnToList }}>
        <Button icon={<Plus className="h-4 w-4" />}>
          Add {w.label || w.abbreviation} Case
        </Button>
      </Link>
    );
  }

  // Prefer the currently-selected workflow (from the tab strip) as
  // the primary target — cheapest click for the common case.
  const primary =
    initiatableWorkflows.find((w) => w.abbreviation === selectedAbv) ??
    initiatableWorkflows[0];
  const primaryLabel = primary.label || primary.abbreviation;
  const primaryHref = buildNewCaseHref(primary.abbreviation);

  return (
    <div className="relative" ref={containerRef}>
      <div className="inline-flex">
        <Link to={primaryHref} state={{ from: returnToList }}>
          <Button icon={<Plus className="h-4 w-4" />} className="rounded-r-none">
            Add {selectedLabel || primaryLabel} Case
          </Button>
        </Link>
        <Button
          variant="primary"
          className="rounded-l-none border-l border-white/20 px-2"
          aria-label="Choose a workflow"
          onClick={() => setOpen((v) => !v)}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-rgf-gray-200 bg-white shadow-lg">
          {initiatableWorkflows.map((w) => (
            <Link
              key={w.abbreviation}
              to={buildNewCaseHref(w.abbreviation)}
              state={{ from: returnToList }}
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-rgf-gray-700 hover:bg-rgf-gray-50 hover:text-rgf-gray-900"
            >
              Add {w.label || w.abbreviation} Case
              {w.requires_existing_candidate && (
                <span className="ml-2 text-xs text-rgf-gray-500">(needs candidate)</span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Parse a positive integer out of a query-string value with a
 * conservative fallback. Handles both undefined (missing param) and
 * malformed input (a URL someone typed by hand).
 */
function parsePositiveInt(raw: string | null, fallback: number): number {
  if (!raw) return fallback;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return n;
}
